import { Currency } from 'core/agency/Agency';

export enum ReportDimension {
  MONTH = 'month',
  DAY = 'day',
  HOUR = 'hour',
  AGENCY = 'agencyId',
  ADVERTISER = 'advertiserId',
  ORDER = 'adsOrderId',
  CAMPAIGN = 'campaignId',
  L2_OBJECT = 'l2ObjectId',
  L1_OBJECT = 'l1ObjectId',
  CAMPAIGN_TYPE = 'adType',
  SALES_CHANNEL = 'salesChannel',
  CREATIVE = 'creativeId',
  L3_CHANNEL = 'l3ChannelId',
  CREATIVE_TYPE = 'creativeType',
  CREATIVE_SIZE = 'creativeSize',
  SELF_BUY = 'isSelfBuy',
  DEVICE_TYPE = 'deviceType',
  MRT = 'mrt'
}

export enum ReportCompareDimension {
  SALES_CHANNEL = 'salesChannel',
  CREATIVE = 'creativeId'
}

export enum ReportType {
  REBATE = 'rebate',
  PERFORMANCE = 'performance',
  VIDEO = 'video',
  OUTDOOR = 'outdoor',
  LINE_MESSAGE = 'lineMessage'
}

export enum ReportGran {
  MONTH = 'month',
  DAY = 'day',
  HOUR = 'hour'
}

export enum Metrics {
  IMPRES = 'impres',
  VIEWABLE = 'viewable',
  CLICKS = 'clicks',
  VIEW = 'adView',
  VIEW_RATE = 'viewRate',
  VIEWABLE_VIEW_RATE = 'viewableViewRate',
  CPV = 'cpv',
  CTR = 'ctr',
  VCTR = 'vctr',
  SKIP_COUNT = 'skipCount',
  UNDER_BID_FLOOR = 'underBidFloor',
  FIRST_QUARTILE = 'firstQuartile',
  MID_POINT = 'midpoint',
  THIRD_QUARTILE = 'thirdQuartile',
  COMPLETE = 'complete',
  SPENT = 'spent',
  CONV_1 = 'conv_1',
  CONV_7 = 'conv_7',
  CONVS = 'convs',
  CVR = 'cvr',
  MEDIA_COST = 'mediaCost',
  MEDIA_SPENT = 'mediaSpent',
  AGENCY_PROFIT = 'agcProfit',
  SYS_PROFIT = 'sysProfit',
  DATA_COST = 'dataCost',
  CPM = 'cpm',
  CPC = 'cpc',
  CPA = 'cpa',
  UU = 'uu',
  BEACON_EVENTS = 'beaconEvents',
  E_PUBLISHER_IMPRES = 'ePublisherImpres',
  BEACON = 'beacon',
  MESSAGE_COUNT = 'messageCount'
}

export type ReportRecord = {
  dimensionID?: string;
  dimensionName: string;
  agcProfit: number;
  clicks: number;
  complete?: number;
  conv_1: number;
  conv_7: number;
  convs: number;
  dataCost: number;
  firstQuartile?: number;
  midpoint?: number;
  thirdQuartile?: number;
  impres: number;
  incentive?: number;
  mediaCost: number;
  mediaSpent?: number;
  skipCount?: number;
  spent: number;
  sysProfit: number;
  underBidFloor?: number;
  view?: number;
  viewable: number;
  winTotal: number;
  tags: Array<string>;
  uu?: number;
  parentId?: number | string;
  extra?: { [key: string]: any };
  beaconEvents?: number;
  beacon?: number;
  messageCount?: number;
};

export type ReportCompareRecord = ReportRecord & {
  compareID: string;
  compareName: string;
};

export type ReportData = {
  allowMetrics: Array<Metrics>;
  dimension: ReportDimension;
  filter: any;
  from: string;
  to: string;
  records: Array<ReportRecord>;
  currency: Currency;
};

export type OutdoorDeviceReportTableRecord = {
  deliveryTargetName: string;
} & Record<Exclude<string, 'deliveryTargetName'>, number>;

export type OutdoorDeviceReportData = {
  dimension: string;
  from: string;
  to: string;
  metricType: OutdoorDeviceMetricType;
  records: OutdoorDeviceReportTableRecord[];
};

export type ReportCompareData = {
  allowMetrics: Array<Metrics>;
  dimension: ReportDimension;
  filter: any;
  from: string;
  to: string;
  records: Array<ReportCompareRecord>;
  currency: Currency;
};

export type RTBBaseReportTableData = {
  id: string;
  key: string;
  name: string;
  parentId?: number | string;
  extra?: any;
  tags: string[];
  impres: number;
  clicks: number;
  convs: number;
  conv_1: number;
  conv_7: number;
  mediaCost: number;
  mediaSpent: number;
  agcProfit: number;
  sysProfit: number;
  dataCost: number;
  spent: number;
};

export type GeneralReportTableData = {
  ctr: number;
  viewable: number;
  vctr: number;
  cpc: number;
  cpm: number;
  cpa: number;
  cvr: number;
  uu: number;
} & RTBBaseReportTableData;

export type VideoReportTableData = {
  adView: number;
  viewRate: number;
  viewable: number;
  viewableViewRate: number;
  vctr: number;
  cpv: number;
  skipCount: number;
  firstQuartile: number;
  midpoint: number;
  thirdQuartile: number;
  complete: number;
  cvr: number;
} & RTBBaseReportTableData;

export type OutdoorReportTableData = {
  id: string;
  key: string;
  extra?: any;
  name: string;
  tags: string[];
  impres: number;
  adView: number;
  viewRate: number;
  cpm: number;
  firstQuartile: number;
  midpoint: number;
  thirdQuartile: number;
  complete: number;
  ePublisherImpres: number;
  agcProfit: number;
  spent: number;
};

export type RebateReportTableData = {
  viewable: number;
  convs: number;
  conv_1: number;
  conv_7: number;
} & RTBBaseReportTableData;

export type BeaconReportTableData = {
  id: string;
  key: string;
  name: string;
  beacon?: number;
  messageCount?: number;
  spent: number;
  tags: string[];
};

export enum OutdoorDeviceMetricType {
  E_PUBLISHER_IMPRES = 'ePublisherImpres',
  IMPRES = 'impres'
}

export type ReportTableData =
  | GeneralReportTableData
  | VideoReportTableData
  | RebateReportTableData
  | BeaconReportTableData;
